/**
 * Set Configurator - Web Component für Starter Sets
 * Lazy-loaded und nur auf Set-Produktseiten aktiv
 */

const GDN_SLOT_COUNT_PATTERNS = [
  /(\d+)\s*Farbe/i,
  /(\d+)\s*Top\s*Coats?/i,
];

function gdnParseSlotCountFromText(text) {
  if (!text) return null;
  const value = String(text);
  for (const pattern of GDN_SLOT_COUNT_PATTERNS) {
    const match = value.match(pattern);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  return null;
}

class SetConfigurator extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.flickityLoaded = false;
    this.fuseLoaded = false;
    this.observer = null;
    this.priceObserverInterval = null;
    this.activeColorCount = null;
  }

  async connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    // Metafield-Daten aus data-Attributen holen
    this.colorOptions = this.dataset.colorOptions || "";
    this.customFields = this.dataset.customFields || "";
    this.productId = this.dataset.productId || "";

    // Prüfen ob color_options vorhanden
    if (
      !this.colorOptions ||
      this.colorOptions === "" ||
      this.colorOptions === "null"
    ) {
      this.handleNoColorOptions();
      return;
    }

    try {
      this.parsedColorOptions = JSON.parse(this.colorOptions);
      this.parsedCustomFields = this.customFields
        ? JSON.parse(this.customFields)
        : null;
    } catch (e) {
      console.warn("SetConfigurator: Invalid JSON data", e);
      this.handleNoColorOptions();
      return;
    }

    // Event-basierte Initialisierung statt Polling
    await this.waitForHulkApps();
    await this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  // Hilfsfunktion: Entfernt "[ €0.0 ]", "[ €12.50 ]" etc. aus Texten
  cleanPriceText(text) {
    if (!text) return "";
    return text.replace(/\s*\[\s*€[\d.,]+\s*\]/g, "").trim();
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.priceObserverInterval) {
      clearInterval(this.priceObserverInterval);
      this.priceObserverInterval = null;
    }
    if (this._onVariantChange) {
      document.removeEventListener("variant:change", this._onVariantChange);
      this._onVariantChange = null;
    }
    if (this._onProductRerender) {
      document.removeEventListener("product:rerender", this._onProductRerender);
      this._onProductRerender = null;
    }
    if (this._onNextButtonClick) {
      document.removeEventListener("click", this._onNextButtonClick, true);
      this._onNextButtonClick = null;
    }
    if (this._onCarouselPointerOver) {
      document.removeEventListener(
        "pointerover",
        this._onCarouselPointerOver
      );
      this._onCarouselPointerOver = null;
    }
    if (this._carouselObserver) {
      this._carouselObserver.disconnect();
      this._carouselObserver = null;
    }
    if (this._carouselMutationObserver) {
      this._carouselMutationObserver.disconnect();
      this._carouselMutationObserver = null;
    }
  }

  /**
   * Extrahiert die Farbanzahl aus einer Variante.
   *
   * Reihenfolge:
   * 1. Pattern `(\d+)\s*Farbe` in option1/2/3 des Variant-Objekts.
   * 2. Fallback: angereicherter Datensatz aus `script[data-variant-detail-data]`
   *    (siehe variant-detail-tile.liquid - dort wird option1 mit dem
   *    count_label angereichert, wenn die rohe option1 keine Farb-Anzahl
   *    enthält). Dadurch funktioniert das auch für Varianten, die
   *    "Basic"/"Pro"/"Plus" heißen ohne "X Farbe" im Namen.
   * 3. Letzter Versuch: `(\d+)\s*Farbe`-Pattern direkt im count_label des
   *    angereicherten Datensatzes.
   *
   * Returns null, wenn keine der Quellen die Anzahl liefert.
   */
  getSlotConfig() {
    const opts = this.parsedColorOptions || {};
    let slotSingular = opts.slotSingular || opts.slotLabel || "";

    if (!slotSingular) {
      const firstName = opts.options?.[0]?.name || "";
      slotSingular = /top\s*coat/i.test(firstName) ? "Top Coat" : "Farbe";
    }

    const isTopCoat = /top\s*coat/i.test(slotSingular);

    return {
      slotSingular,
      pickerTitle:
        opts.pickerTitle ||
        (isTopCoat ? "Wähle deinen Top Coat:" : "Wähle deine Farbe:"),
      nextPickBanner:
        opts.nextPickBanner ||
        (isTopCoat
          ? "Bitte wähle deinen nächsten Top Coat"
          : "Bitte wählen Sie Ihre nächste Farbe"),
      chooseButtonPrefix:
        opts.chooseButtonPrefix ||
        (isTopCoat ? "Wähle deinen Top Coat" : "Wähle deine Farbe"),
    };
  }

  getDefaultSlotName(index) {
    const { slotSingular } = this.getSlotConfig();
    if (/top\s*coat/i.test(slotSingular)) {
      return `Top Coat ${String(index).padStart(2, "0")}`;
    }
    return `${slotSingular} ${index}`;
  }

  applyPickerCopy() {
    const { pickerTitle, nextPickBanner } = this.getSlotConfig();
    const titleEl = this.querySelector(".custom-options-title");
    const bannerVarText = this.querySelector("#bannerVariantText");

    if (titleEl) {
      titleEl.textContent = pickerTitle;
    }

    if (bannerVarText) {
      bannerVarText.textContent = nextPickBanner;
    }

    this.getDataCount();
  }

  getColorCountFromVariant(variant) {
    if (!variant) return null;

    const options = [variant.option1, variant.option2, variant.option3];
    for (const option of options) {
      const count = gdnParseSlotCountFromText(option);
      if (count !== null) {
        return count;
      }
    }

    return this._lookupCountFromVariantDetail(variant.id);
  }

  /**
   * Sucht in allen `script[data-variant-detail-data]`-Tags nach dem Variant
   * mit der gegebenen ID und versucht, daraus die Farb-Anzahl zu extrahieren.
   * Diese Tags werden in variant-detail-tile.liquid pro product_form_id
   * gerendert und enthalten ein angereichertes option1 + count_label.
   */
  _lookupCountFromVariantDetail(variantId) {
    if (!variantId) return null;

    const scripts = document.querySelectorAll(
      "script[data-variant-detail-data]"
    );
    for (const script of scripts) {
      let parsed;
      try {
        parsed = JSON.parse(script.textContent || "[]");
      } catch (e) {
        continue;
      }
      if (!Array.isArray(parsed)) continue;

      const entry = parsed.find((v) => v && v.id === variantId);
      if (!entry) continue;

      const candidates = [
        entry.option1,
        entry.option2,
        entry.option3,
        entry.count_label,
      ];
      for (const candidate of candidates) {
        const count = gdnParseSlotCountFromText(candidate);
        if (count !== null) {
          return count;
        }
      }
    }
    return null;
  }

  /**
   * Liest die Farbanzahl aus der initial gewählten Variante beim Seitenaufruf.
   *
   * Reihenfolge:
   * 1. Variant-Pattern: `(\d+)\s*Farbe` in option1/2/3 (z.B. "3 Farben Set").
   * 2. Fallback: Anzahl der serverseitig gerenderten Slots im DOM
   *    (.custom-options-enterprise .option:not([data-new-container])).
   *    Das Liquid in custom-hulk-drawer.liquid hat einen eigenen Fallback auf
   *    color_data.options.size, falls der Variant-Name nichts hergibt - das
   *    Server-Rendering ist also die zuverlässige Source of Truth, wenn der
   *    Variant-Name keine Farb-Anzahl enthält (z.B. "Basic"/"Pro"/"Plus").
   *
   * Ohne Fix B greift der Schutz in checkAndAppendContainers nicht, sobald
   * activeColorCount null ist - dann erscheint nach dem Befüllen aller Slots
   * fälschlich ein zusätzlicher "Optionale Farbe"-Slot.
   */
  getInitialColorCount() {
    const variantScript = document.querySelector("script[data-variant]");
    if (variantScript) {
      try {
        const variant = JSON.parse(variantScript.textContent);
        const fromVariant = this.getColorCountFromVariant(variant);
        if (fromVariant !== null) return fromVariant;
      } catch (e) {
        // fall through to DOM-Fallback
      }
    }

    const serverSlots = this.querySelectorAll(
      ".custom-options-enterprise .option:not([data-new-container])"
    ).length;
    return serverSlots > 0 ? serverSlots : null;
  }

  /**
   * Entfernt überschüssige Container beim initialen Laden (bevor Setup-Methoden laufen).
   */
  trimContainersToCount(count) {
    const enterprise = this.querySelector(".custom-options-enterprise");
    if (!enterprise) return;

    const containers = enterprise.querySelectorAll(".option");
    for (let i = containers.length - 1; i >= count; i--) {
      containers[i].remove();
    }
  }

  /**
   * Setzt die Farbauswahl EINES Containers zurück: entfernt das Bild, setzt den
   * Text auf den Original-Wert, blendet den Close-Button aus und resettet die
   * gekoppelte Hulk-Swatch-Property (damit nichts ungewollt im Cart landet).
   */
  _clearSelectionForContainer(container) {
    if (!container) return;

    const img = container.querySelector(".design img");
    const design = container.querySelector(".design");
    const paragraph = container.querySelector("p");
    const closeBtn = container.querySelector(".deleteOption");

    if (img) {
      img.remove();
      if (design) {
        design.classList.remove("hide-after");
        design.style.backgroundColor = "#f1edea";
      }
    }

    if (paragraph) {
      const original =
        paragraph.getAttribute("data-original-text") || paragraph.textContent;
      paragraph.textContent = original;
    }

    if (closeBtn) {
      closeBtn.style.display = "none";
    }

    this.updateSwatchPriceOnClose(container);
  }

  /**
   * Setzt alle Farbauswahlen in allen Containern zurück (Bilder, Texte, Hulk-Swatches).
   */
  clearAllSelections() {
    this.querySelectorAll(".custom-options-enterprise .option").forEach(
      (container) => this._clearSelectionForContainer(container)
    );
  }

  /**
   * Lauscht auf Varianten-Wechsel und passt die Farbcontainer-Anzahl an.
   *
   * Zwei zusätzliche Sicherheitsnetze für den "Wähle deine Farbe"-Button:
   * 1. Auf product:rerender wird createNextContainerButton() neu aufgerufen,
   *    weil <product-rerender> die <product-payment-container> samt #nextContainerButton
   *    austauscht (theme.js Zeile 5390-5391) und damit den ursprünglichen Click-Listener killt.
   * 2. Click-Delegation auf document in Capture-Phase: fängt Klicks auf
   *    #nextContainerButton / #sticky_nextContainerButton ab, BEVOR der Form-Submit
   *    feuern könnte, und ruft openNextContainerWithoutImage() direkt auf.
   *    Damit ist der Button auch bei Race-Conditions sicher klickbar.
   */
  setupVariantListener() {
    /**
     * Re-sync Button-Sichtbarkeit + -Text NACHDEM das Theme die Buttons neu
     * gerendert hat. Nötig, weil <product-rerender> beim Variant-Wechsel
     * <product-payment-container> komplett austauscht (theme.js ~5390) und
     * der neue #nextContainerButton mit dem Liquid-Default kommt:
     * "display: block" + Text "Wähle deine Farbe 01". Ohne diesen Re-Sync
     * sieht der User beim Wechsel von Pro (alle 6 Slots gefüllt) auf Basic
     * (3 Slots, weiter gefüllt) den Wähle-Farbe-Button statt Add-to-Cart.
     */
    this._resyncAfterRerender = () => {
      this.createNextContainerButton();
      this.updateBannerMessage();
      this.getDataCount();
    };

    this._onVariantChange = (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;

      const colorCount = this.getColorCountFromVariant(variant);
      if (colorCount !== null) {
        this.updateContainerCount(colorCount);
      }

      // Fallback-Re-Sync: das Theme dispatcht `product:rerender` direkt nach
      // `variant:change` auf dem <form>-Element (OHNE bubbles, daher nicht
      // auf document erreichbar). Wir warten kurz und re-syncen die Button-
      // Sichtbarkeit, falls der direkte Form-Listener (siehe unten) noch
      // nicht gebunden war oder der rerender länger dauert.
      setTimeout(() => this._resyncAfterRerender(), 250);
    };

    /**
     * Direktes Lauschen auf <form>-Ebene. `product:rerender` bubblet NICHT,
     * deshalb müssen wir den Listener direkt am Form anhängen statt am
     * document. Falls das Form (noch) nicht im DOM ist, polled wir kurz mit
     * MutationObserver und binden, sobald es da ist.
     */
    this._onProductRerender = () => this._resyncAfterRerender();

    const bindFormRerender = (form) => {
      if (!form || form.dataset.setConfiguratorRerenderBound === "1") return;
      form.dataset.setConfiguratorRerenderBound = "1";
      form.addEventListener("product:rerender", this._onProductRerender);
    };

    const findAndBindForm = () => {
      const form = document.querySelector(
        "form[is='product-form'], form.shopify-product-form"
      );
      if (form) {
        bindFormRerender(form);
        return true;
      }
      return false;
    };

    if (!findAndBindForm()) {
      // Form noch nicht im DOM (z.B. async Theme-Init) → einmalig Observer.
      const observer = new MutationObserver(() => {
        if (findAndBindForm()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      // Sicherheits-Cap: nach 5 s aufgeben, damit kein Dauer-Observer läuft.
      setTimeout(() => observer.disconnect(), 5000);
    }

    this._onNextButtonClick = (event) => {
      const btn = event.target.closest(
        "#nextContainerButton, #sticky_nextContainerButton"
      );
      if (!btn) return;

      event.preventDefault();
      event.stopPropagation();

      if (typeof this.openNextContainerWithoutImage === "function") {
        this.openNextContainerWithoutImage();
      }
    };

    document.addEventListener("variant:change", this._onVariantChange);
    document.addEventListener("click", this._onNextButtonClick, true);
  }

  /**
   * Passt die Anzahl der Farbcontainer dynamisch an (z.B. bei Variant-Wechsel).
   *
   * Verhalten:
   * - Slots 0..count-1 behalten ihre aktuelle Auswahl. Damit bleibt die
   *   Auswahl beim Wechsel von z.B. Pro (5 Farben) zu Plus (3 Farben)
   *   erhalten - nur die überschüssigen Slots 4+5 werden entfernt.
   * - Beim Verkleinern: die zu entfernenden Slots werden VOR dem Entfernen
   *   geleert (inkl. Hulk-Property-Reset über _clearSelectionForContainer),
   *   damit ihre versteckten Hulk-Selektionen nicht im Cart landen.
   * - Beim Vergrößern: neue leere Slots werden hinten angehängt.
   *
   * Bonus-Slots ([data-new-container]) werden nicht von dieser Logik berührt.
   */
  updateContainerCount(count) {
    this.activeColorCount = count;

    const enterprise = this.querySelector(".custom-options-enterprise");
    if (!enterprise) return;

    const existingContainers = enterprise.querySelectorAll(".option");
    const currentCount = existingContainers.length;

    if (currentCount > count) {
      for (let i = currentCount - 1; i >= count; i--) {
        const slot = existingContainers[i];
        // _pendingSlot könnte auf den zu entfernenden Slot zeigen - dann
        // zurücksetzen, sonst zielt handleSwatchClick auf einen detached node.
        if (this._pendingSlot === slot) {
          this._pendingSlot = null;
        }
        this._clearSelectionForContainer(slot);
        slot.remove();
      }
    }

    if (currentCount < count) {
      const options = this.parsedColorOptions?.options || [];
      for (let i = currentCount + 1; i <= count; i++) {
        const optionData = options[i - 1];
        const name = optionData?.name || this.getDefaultSlotName(i);

        const optionHtml = `
          <div class="option option-${i}" data-count="${i}">
            <div class="design design-${i}"></div>
            <p data-original-text="${name}">${name}</p>
            <div class="deleteOption design-${i}" style="display: none;">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </div>
          </div>
        `;
        enterprise.insertAdjacentHTML("beforeend", optionHtml);

        const newContainer = enterprise.querySelector(`.option-${i}`);
        const closeBtn = newContainer?.querySelector(".deleteOption");
        if (closeBtn) {
          closeBtn.addEventListener("click", (e) =>
            this.handleCloseClick(e, closeBtn)
          );
        }

        setTimeout(() => {
          newContainer?.classList.add("animate");
        }, (i - currentCount) * 100);
      }
    }

    this.syncHulkOptions(count);
    this.setupEventListeners();
    this.updateSelectionStatus();
    this.updateOptionIndexes();
    this.updateButtonVisibility();
    this.checkMatchingOpt();
    this.setupSwatchListeners();
    this.setupDrawer();
    this.createCustomContainerLabels();
    this.getCurrentActiveCont();
    this.getDataCount();
    this.updateBannerMessage();
  }

  /**
   * Blendet Hulk-Swatch-Optionsets ein/aus, passend zur gewählten Farbanzahl.
   *
   * WICHTIG: zusätzlich zum CSS-Verstecken werden die `<input name="...">`-
   * Felder in den versteckten Sets `disabled` gesetzt. Browser submitten
   * `display: none`-Inputs ganz normal mit dem Form - ohne `disabled` würden
   * also beim Wechsel von z.B. Pro (3 gewählte Farben) auf Basic (1 Farbe)
   * trotzdem alle 3 Farben als Line-Item-Properties im Cart landen, obwohl
   * der Add-to-Cart den günstigeren Basic-Preis bucht. Mit `disabled` werden
   * nur die N tatsächlich aktiven Farben mitgeschickt. Beim Wechsel auf eine
   * größere Variante (Basic→Pro) werden die Inputs wieder enabled, sodass
   * spätere Auswahlen wieder funktionieren.
   */
  syncHulkOptions(count) {
    const hulkOptions = document.querySelectorAll(
      ".hulkapps_option_set .hulkapps_option.swatch_render"
    );

    hulkOptions.forEach((opt, index) => {
      const isActive = index < count;
      opt.style.display = isActive ? "" : "none";

      const inputs = opt.querySelectorAll(
        "input[name], select[name], textarea[name]"
      );
      inputs.forEach((input) => {
        input.disabled = !isActive;
      });
    });
  }

  handleNoColorOptions() {
    const farbeParent = this.querySelector("#farbeParent");
    const loadAssets = this.querySelector(".load-assets");
    const addToCart = document.querySelector(
      ".product-form__payment-container #AddToCart"
    );
    const quantitySelector = document.querySelector(".product-form__quantity");

    if (farbeParent) farbeParent.remove();
    if (loadAssets) loadAssets.style.display = "none";
    if (addToCart) addToCart.style.display = "block";
    if (quantitySelector) quantitySelector.style.display = "block";
  }

  /**
   * Wartet auf HulkApps Custom Options - Event-basiert statt Polling
   */
  waitForHulkApps() {
    return new Promise((resolve) => {
      const checkHulkApps = () => {
        const hulkContainer = document.querySelector(".hulkapps_option_set");
        if (hulkContainer) {
          resolve();
          return true;
        }
        return false;
      };

      // Sofort prüfen
      if (checkHulkApps()) return;

      // MutationObserver für HulkApps - viel effizienter als setInterval
      const targetNode = document.body;
      const config = { childList: true, subtree: true };

      const observerCallback = (mutationsList, obs) => {
        if (checkHulkApps()) {
          obs.disconnect();
        }
      };

      const observer = new MutationObserver(observerCallback);
      observer.observe(targetNode, config);

      // Timeout nach 10 Sekunden
      setTimeout(() => {
        observer.disconnect();
        resolve(); // Trotzdem initialisieren
      }, 10000);
    });
  }

  async init() {
    const customOptionsEnterprise = this.querySelector(
      ".custom-options-enterprise"
    );
    const farbeParent = this.querySelector("#farbeParent");

    if (!customOptionsEnterprise) {
      console.warn("SetConfigurator: .custom-options-enterprise not found");
      return;
    }

    // Zeige Container
    if (farbeParent) farbeParent.style.display = "block";

    // Alle Initialisierungen ausführen
    this.appendContainersFromMetafields();

    // Container-Anzahl an initial gewählte Variante anpassen
    const initialCount = this.getInitialColorCount();
    if (initialCount !== null) {
      this.activeColorCount = initialCount;
      this.trimContainersToCount(initialCount);
    }

    this.setupEventListeners();
    this.updateSelectionStatus();
    this.updateOptionIndexes();
    this.updateButtonVisibility();
    this.checkMatchingOpt();
    this.setupSwatchListeners();
    this.setupDrawer();
    this.createNextContainerButton();
    this.createCustomLabels();
    this.createCustomContainerLabels();
    this.getCurrentActiveCont();
    this.initCustomFields();
    this.calculateDifference();
    this.observePriceChanges();
    this.getDataCount();
    this.setupVariantListener();
    this.applyPickerCopy();

    // Bilddaten im Hintergrund prefetchen (non-blocking).
    this.fetchColorProductImages();

    // Karussells nur für sichtbare Swatches bauen (IntersectionObserver).
    // Hover-Listener als Fallback falls der User schneller hovert als der Build.
    this._setupCarouselObserver();
    this._setupCarouselHovers();

    // Lazy-load Fuse.js für Suche
    await this.initSearch();

    // Loader verstecken
    const loadAssets = this.querySelector(".load-assets");
    if (loadAssets) loadAssets.style.display = "none";

    // Quantity Selector verstecken
    const quantitySelector = document.querySelector(".product-form__quantity");
    if (quantitySelector) quantitySelector.style.display = "none";
  }

  /**
   * Lazy-Load Flickity nur wenn benötigt
   */
  async loadFlickity() {
    if (this.flickityLoaded || window.Flickity) {
      this.flickityLoaded = true;
      return;
    }

    return new Promise((resolve) => {
      // CSS laden
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/flickity@2/dist/flickity.min.css";
      document.head.appendChild(link);

      // JS laden
      const script = document.createElement("script");
      script.src = "https://unpkg.com/flickity@2/dist/flickity.pkgd.min.js";
      script.onload = () => {
        this.flickityLoaded = true;
        resolve();
      };
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
  }

  /**
   * Lazy-Load Fuse.js nur wenn benötigt
   */
  async loadFuse() {
    if (this.fuseLoaded || window.Fuse) {
      this.fuseLoaded = true;
      return;
    }

    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/fuse.js@6.6.2/dist/fuse.min.js";
      script.onload = () => {
        this.fuseLoaded = true;
        resolve();
      };
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
  }

  /**
   * Container aus Metafields erstellen - nur wenn noch nicht serverseitig gerendert
   */
  appendContainersFromMetafields() {
    const customOptionsEnterprise = this.querySelector(
      ".custom-options-enterprise"
    );
    if (!customOptionsEnterprise || !this.parsedColorOptions?.options) return;

    // Prüfen ob Container bereits serverseitig gerendert wurden
    const existingContainers =
      customOptionsEnterprise.querySelectorAll(".option");
    if (existingContainers.length > 0) {
      // Container existieren bereits - nur Animation hinzufügen
      existingContainers.forEach((option, index) => {
        setTimeout(() => {
          option.classList.add("animate");
        }, index * 100);
      });
      return;
    }

    // Fallback: Container dynamisch erstellen wenn nicht serverseitig gerendert
    const options = this.parsedColorOptions.options;

    options.forEach((color, index) => {
      const optionHtml = `
        <div class="option option-${index + 1}" data-count="${index + 1}">
          <div class="design design-${index + 1}"></div>
          <p data-original-text="${color.name}">${color.name}</p>
          <div class="deleteOption design-${index + 1}" style="display: none;">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </div>
        </div>
      `;

      customOptionsEnterprise.insertAdjacentHTML("beforeend", optionHtml);

      // Animate
      setTimeout(() => {
        const option = customOptionsEnterprise.querySelector(
          `.option-${index + 1}`
        );
        if (option) option.classList.add("animate");
      }, index * 100);
    });
  }

  /**
   * Event-Listeners für Close-Buttons und Design-Clicks
   */
  setupEventListeners() {
    // Close Button Handler - nur wenn noch nicht attached
    this.querySelectorAll(".deleteOption").forEach((close) => {
      if (close.dataset.listenerAttached) return;
      close.dataset.listenerAttached = "true";
      close.addEventListener("click", (event) =>
        this.handleCloseClick(event, close)
      );
    });

    // Original Text ist jetzt serverseitig via data-original-text gesetzt
    // Nur für dynamisch erstellte Container nachholen
    this.querySelectorAll(".option p").forEach((paragraph) => {
      if (!paragraph.hasAttribute("data-original-text")) {
        paragraph.setAttribute("data-original-text", paragraph.textContent);
      }
    });

    // Hulk-Icon prevent default
    const hulkIcon = this.querySelector(".hulk-icon");
    if (hulkIcon && !hulkIcon.dataset.listenerAttached) {
      hulkIcon.dataset.listenerAttached = "true";
      hulkIcon.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
      });
    }

    // Close drawer icon
    const closeIcon = this.querySelector(".fa-xmark, .close-drawer-icon");
    if (closeIcon && !closeIcon.dataset.listenerAttached) {
      closeIcon.dataset.listenerAttached = "true";
      closeIcon.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeDrawer();
      });
    }
  }

  handleCloseClick(event, close) {
    event.preventDefault();
    event.stopPropagation();

    const blurOverlay = this.querySelector("#blur-overlay");
    const loader = this.querySelector(".loader");

    if (loader) loader.style.display = "grid";
    if (blurOverlay) blurOverlay.style.display = "block";

    const container = close.parentNode;
    const targetImg = close.closest(".option")?.querySelector("img");
    const siblingEl = close.parentNode.querySelector(".design");
    const paragraph = container.querySelector("p");

    // Bild entfernen
    if (targetImg) {
      targetImg.remove();
      if (siblingEl) {
        siblingEl.classList.remove("hide-after");
        siblingEl.style.backgroundColor = "#f1edea";
      }
    }

    // Original Text wiederherstellen
    if (paragraph) {
      const original =
        paragraph.getAttribute("data-original-text") || paragraph.textContent;
      paragraph.textContent = original;
    }

    // Container nur entfernen wenn er "optinal" ist UND ausserhalb des Varianten-Limits liegt
    if (container?.getAttribute("data-new-container") === "true") {
      const currentContainerCount = this.querySelectorAll(
        ".custom-options-enterprise .option"
      ).length;
      if (
        this.activeColorCount === null ||
        currentContainerCount > this.activeColorCount
      ) {
        container.remove();
      }
    }

    // Swatch-Preis zurücksetzen
    this.updateSwatchPriceOnClose(container);

    this.checkAndAppendContainers();
    this.updateBannerMessage();
    this.getCurrentActiveCont();
    this.updateSelectionStatus();
    this.updateOptionIndexes();
    this.updateButtonVisibility();
    this.getDataCount();

    setTimeout(() => {
      if (loader) loader.style.display = "none";
      if (blurOverlay) {
        blurOverlay.style.display = "none";
        blurOverlay.style.zIndex = "9";
      }
    }, 500);
  }

  updateSwatchPriceOnClose(container) {
    if (!container) return;

    const classList = container.classList;
    classList.forEach((cls) => {
      if (cls.startsWith("option-")) {
        document
          .querySelectorAll(".hulkapps_option_set .hulkapps_option")
          .forEach((set) => {
            const hulkToken = Array.from(set.classList).find((c) =>
              c.startsWith("option-")
            );
            if (cls === hulkToken) {
              const swatches = set.querySelectorAll("label [data-radio-class]");
              const swatchVal = set.querySelector(".hulk_swatch_hidden_prop");

              swatches.forEach((selectedSwatch) => {
                if (selectedSwatch.classList.contains("swatch_selected")) {
                  selectedSwatch.classList.remove("swatch_selected");
                  if (swatchVal) swatchVal.value = "";

                  const input = selectedSwatch.querySelector("input");
                  if (input) {
                    setTimeout(() => {
                      input.dispatchEvent(
                        new Event("change", { bubbles: true, cancelable: true })
                      );
                    }, 500);
                  }
                }
              });
            }
          });
      }
    });
  }

  isContainerFilled(container) {
    return container?.querySelector(".design img") !== null;
  }

  updateSelectionStatus() {
    const containers = this.querySelectorAll(
      ".custom-options-enterprise .option"
    );
    let filledCount = 0;

    containers.forEach((container) => {
      if (this.isContainerFilled(container)) {
        filledCount++;
      }
    });

    const statusElement = this.querySelector("#selection-status");
    if (statusElement) {
      statusElement.textContent = `${filledCount} von ${containers.length} ausgewählt`;
    }
  }

  updateOptionIndexes() {
    const containers = Array.from(
      this.querySelectorAll(".custom-options-enterprise .option")
    );

    containers.forEach((container, newIndex) => {
      // Remove old option- classes
      container.classList.forEach((cls) => {
        if (cls.startsWith("option-")) container.classList.remove(cls);
      });
      container.classList.add(`option-${newIndex + 1}`);

      const designEl = container.querySelector(".design");
      if (designEl) {
        designEl.classList.forEach((cls) => {
          if (cls.startsWith("design-")) designEl.classList.remove(cls);
        });
        designEl.classList.add(`design-${newIndex + 1}`);
      }

      const closeBtnEl = container.querySelector(".deleteOption");
      if (closeBtnEl) {
        closeBtnEl.classList.forEach((cls) => {
          if (cls.startsWith("design-")) closeBtnEl.classList.remove(cls);
        });
        closeBtnEl.classList.add(`design-${newIndex + 1}`);
      }
    });
  }

  updateButtonVisibility() {
    const options = this.querySelectorAll(".custom-options-enterprise .option");

    options.forEach((option) => {
      const design = option.querySelector(".design");
      const clearDesign = option.querySelector(".deleteOption");

      if (design && clearDesign) {
        const hasImage = design.querySelector("img") !== null;
        clearDesign.style.display = hasImage ? "block" : "none";
      }
    });

    // MutationObserver für .custom-options-enterprise - mit begrenztem Scope
    if (!this.buttonObserver) {
      const targetNode = this.querySelector(".custom-options-enterprise");
      if (targetNode) {
        this.buttonObserver = new MutationObserver(() =>
          this.updateButtonVisibility()
        );
        this.buttonObserver.observe(targetNode, {
          childList: true,
          subtree: true,
        });
      }
    }
  }

  checkMatchingOpt() {
    const hulkOptions = document.querySelector(
      '[id^="hulkapps_custom_options_"]'
    );
    const imgContainer = this.querySelector(".img-container");
    const hulkInputWrapper = this.querySelector(".hulk-input-wrapper");
    const topSection = this.querySelector(".top-section");
    const lowerBar = this.querySelector(".lower-bar");
    const lowerSection = this.querySelector(".lower-section");

    if (hulkOptions) hulkOptions.style.display = "block";
    if (imgContainer) imgContainer.textContent = "";
    if (hulkInputWrapper) hulkInputWrapper.style.display = "flex";
    if (topSection) topSection.style.display = "none";
    if (lowerBar) lowerBar.style.display = "none";
    if (lowerSection) lowerSection.style.display = "none";

    this.querySelectorAll(".option").forEach((opt) =>
      opt.classList.remove("active")
    );

    const designs = this.querySelectorAll(
      ".custom-options-enterprise .option .design"
    );
    designs.forEach((design) => {
      design.addEventListener("click", () =>
        this.handleDesignClick(design, designs)
      );
    });
  }

  handleDesignClick(design, allDesigns) {
    const designOptionClass = Array.from(allDesigns).find(
      (d) => !d.querySelector("img")
    );
    const imgElement = design.querySelector("img");

    if (!imgElement && designOptionClass) {
      const getDesignParent = designOptionClass.parentNode;
      setTimeout(() => getDesignParent.classList.add("active"), 200);

      const filteredClass = Array.from(getDesignParent.classList).find((cls) =>
        cls.startsWith("option-")
      );
      const parentHulk = document.querySelector(".hulkapps_option_set");

      if (parentHulk) {
        const optionClasses = parentHulk.querySelectorAll(
          "div.hulkapps_option.swatch_render"
        );

        optionClasses.forEach((opt) => {
          const hulkOption = Array.from(opt.classList).find((c) =>
            c.startsWith("option-")
          );
          const ariaHulk = opt.querySelector("[aria-label]");

          if (filteredClass === hulkOption) {
            setTimeout(() => {
              const topBar = this.querySelector(".top-bar");
              const imgContainer = topBar?.querySelector(".img-container");

              if (imgContainer) {
                imgContainer
                  .querySelectorAll("span")
                  .forEach((span) => span.remove());
                const textElement = document.createElement("span");
                textElement.textContent = this.cleanPriceText(
                  ariaHulk?.getAttribute("aria-label") || ""
                );
                imgContainer.appendChild(textElement);
              }
            }, 700);
          }

          opt.style.display = filteredClass === hulkOption ? "block" : "none";
        });
      }
    }

    this.updateButtonVisibility();
  }

  createNewContainer(index) {
    const newContainer = document.createElement("div");
    newContainer.classList.add("option", `option-${index}`, "discount-farbe");
    newContainer.setAttribute("data-new-container", "true");
    newContainer.innerHTML = `
      <div class="design design-${index}"></div>
      <p>Optionale Farbe</p>
      <div class="deleteOption design-${index}">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </div>
    `;

    // Event listener für neuen Container
    const closeBtn = newContainer.querySelector(".deleteOption");
    closeBtn.addEventListener("click", (e) =>
      this.handleCloseClick(e, closeBtn)
    );

    return newContainer;
  }

  checkAndAppendContainers() {
    const options = this.querySelectorAll(".custom-options-enterprise .option");
    const hulkOptions = document.querySelectorAll(
      '[class^="hulkapps_option swatch_render"]'
    );
    const customCount = options.length;
    const hulkCount = hulkOptions.length;
    const lastContainer = options[options.length - 1];

    if (this.activeColorCount !== null && customCount >= this.activeColorCount) {
      return;
    }

    // Zusätzlicher Schutz: auch wenn activeColorCount null ist, NIE einen Bonus-Slot
    // anhängen. Bonus-Slots ("Optionale Farbe") führen dazu, dass der User-Auswahl-
    // Pointer (firstEmpty) immer auf den neuen leeren Bonus-Slot zeigt - damit kann
    // der User vorhandene Slots nicht mehr austauschen, weil setActiveSlot stets den
    // Bonus-Slot als ersten leeren findet (Stand: User-Feedback Apr 2026).
    // Falls "Optionale Farbe"-Slots irgendwann wieder gewollt sind, muss das auf einen
    // explizit konfigurierbaren Modus umgestellt werden.
    if (this.activeColorCount === null) {
      return;
    }

    const lastContainerHasImage = lastContainer?.querySelector(".design img");
    const designBanner = this.querySelector("#designBanner");

    if (
      designBanner &&
      (!designBanner.style.display || designBanner.style.display === "none")
    ) {
      setTimeout(() => designBanner.classList.add("show"), 500);
    }

    const existingContainers = this.querySelectorAll(
      ".custom-options-enterprise .option:not([data-new-container])"
    );
    const existingContainersWithImages = Array.from(existingContainers).filter(
      (opt) => opt.querySelector(".design img") !== null
    ).length;

    if (hulkCount > customCount && lastContainerHasImage) {
      if (existingContainersWithImages === existingContainers.length) {
        const newContainer = this.createNewContainer(customCount + 1);
        this.querySelector(".custom-options-enterprise").appendChild(
          newContainer
        );

        this.updateBannerMessage();
        this.updateButtonVisibility();
        this.setupEventListeners();
        this.checkMatchingOpt();
        this.setupSwatchListeners();
        this.createCustomContainerLabels();
        this.setupDrawer();
      }
    }
  }

  /**
   * Markiert visuell den aktiven Slot mit schwarzem Rahmen und weißem Hintergrund
   * ("next-container"-Style).
   *
   * Auswahl-Reihenfolge:
   * 1. Erster leerer Server-Slot - der visuelle "klick mich für die nächste
   *    Farbe"-Hinweis. Bonus-Slots ([data-new-container]) werden ignoriert.
   * 2. Wenn alle Server-Slots gefüllt sind: der aktive Slot (.option.active),
   *    den der User für gezielten Austausch markiert hat.
   * 3. Sonst kein Rahmen.
   *
   * Hinweis: Beim gezielten Austausch eines bereits gefüllten Slots wandert
   * der Rahmen visuell zum nächsten leeren Slot (= Default-Hinweis), nicht
   * zum gerade getauschten - das ist okay, weil das Austausch-Ziel über
   * `_pendingSlot` (siehe setActiveSlot) entkoppelt ist und nicht von der
   * CSS-Klasse abhängt.
   */
  getCurrentActiveCont() {
    const containers = this.querySelectorAll(
      ".custom-options-enterprise .option"
    );

    const firstEmpty = Array.from(
      this.querySelectorAll(
        ".custom-options-enterprise .option:not([data-new-container])"
      )
    ).find((container) => !container.querySelector(".design img"));

    let activeContainer = firstEmpty;
    if (!activeContainer) {
      activeContainer = Array.from(containers).find((c) =>
        c.classList.contains("active")
      );
    }

    containers.forEach((container) => {
      const design = container.querySelector(".design");
      const img = design?.querySelector("img");

      if (design) {
        design.style.border = "";
        design.style.backgroundColor = "#f1edea";
        design.classList.remove("next-container");

        if (img) {
          design.style.backgroundImage = "none";
        } else {
          design.style.backgroundImage =
            "url('https://cdn.shopify.com/s/files/1/0832/5008/6221/files/new-nail-bg.webp?v=1739014172')";
        }
      }
    });

    if (activeContainer) {
      const getActiveDesign = activeContainer.querySelector(".design");
      if (getActiveDesign) {
        getActiveDesign.style.border = "1px solid black";
        getActiveDesign.style.backgroundColor = "white";
        getActiveDesign.classList.add("next-container");
      }
    }
  }

  updateBannerMessage() {
    const bannerText = this.querySelector("#bannerText");
    const bannerParent = this.querySelector("#designBanner");
    const bannerVarText = this.querySelector("#bannerVariantText");
    const activeText = this.querySelector(
      ".custom-options-enterprise .option.active p"
    );
    const addToCartButton = document.querySelector(
      ".product-form__payment-container button#AddToCart"
    );
    const stickyAddToCartButton = document.querySelector(
      ".product-sticky-form__form button#StickyAddToCart"
    );
    const nextContainerButton = document.getElementById("nextContainerButton");
    const stickyNextContainerButton = document.getElementById(
      "sticky_nextContainerButton"
    );
    const paymentButtons = document.querySelector(".shopify-payment-button");

    const existingContainers = this.querySelectorAll(
      ".custom-options-enterprise .option:not([data-new-container])"
    );
    const existingContainersWithImages = Array.from(existingContainers).filter(
      (opt) => opt.querySelector(".design img") !== null
    ).length;

    if (activeText && bannerVarText) {
      bannerVarText.textContent = `${this.cleanPriceText(
        activeText.textContent
      )} erfolgreich ausgewählt`;
    }

    if (existingContainersWithImages === existingContainers.length) {
      setTimeout(() => bannerParent?.classList.add("show"), 500);
      if (bannerText)
        bannerText.textContent = "Toller Look! Dein Set ist jetzt bereit!";
      if (addToCartButton) addToCartButton.style.display = "block";
      if (paymentButtons) paymentButtons.style.display = "block";
      if (stickyAddToCartButton) stickyAddToCartButton.style.display = "block";
      if (stickyNextContainerButton)
        stickyNextContainerButton.style.display = "none";
      if (nextContainerButton) nextContainerButton.style.display = "none";
    } else if (existingContainersWithImages >= 1) {
      if (bannerText)
        bannerText.textContent = "Toller Look! Wähle dein nächstes Design";
      setTimeout(() => bannerParent?.classList.add("show"), 500);
      if (addToCartButton) addToCartButton.style.display = "none";
      if (paymentButtons) paymentButtons.style.display = "none";
      if (stickyAddToCartButton) stickyAddToCartButton.style.display = "none";
      if (stickyNextContainerButton)
        stickyNextContainerButton.style.display = "block";
      if (nextContainerButton) nextContainerButton.style.display = "block";
    } else {
      setTimeout(() => bannerParent?.classList.remove("show"), 500);
      if (addToCartButton) addToCartButton.style.display = "none";
      if (paymentButtons) paymentButtons.style.display = "none";
      if (stickyAddToCartButton) stickyAddToCartButton.style.display = "none";
      if (stickyNextContainerButton)
        stickyNextContainerButton.style.display = "block";
      if (nextContainerButton) nextContainerButton.style.display = "block";
    }
  }

  getDataCount() {
    setTimeout(() => {
      // Finde den nächsten leeren Container (ohne Bild)
      const containers = this.querySelectorAll(
        ".custom-options-enterprise .option"
      );
      const nextEmptyContainer = Array.from(containers).find(
        (container) => !container.querySelector(".design img")
      );

      let dataCount = "01";
      if (nextEmptyContainer) {
        dataCount = nextEmptyContainer.getAttribute("data-count") || "01";
      }

      // Formatiere die Nummer (01, 02, 03, etc.)
      const formattedCount = dataCount.toString().padStart(2, "0");

      const { chooseButtonPrefix } = this.getSlotConfig();
      const buttons = document.querySelectorAll(".hulk-custom");
      buttons.forEach((btn) => {
        btn.textContent = `${chooseButtonPrefix} ${formattedCount}`;
      });
    }, 100);
  }

  createNextContainerButton() {
    // Buttons werden jetzt serverseitig in product-form.liquid gerendert
    // Hier nur Event-Listener hinzufügen wenn sie noch nicht vorhanden sind
    const nextContainerButton = document.getElementById("nextContainerButton");
    const stickyNextContainerButton = document.getElementById(
      "sticky_nextContainerButton"
    );

    if (nextContainerButton && !nextContainerButton.dataset.listenerAttached) {
      nextContainerButton.dataset.listenerAttached = "true";
      nextContainerButton.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.openNextContainerWithoutImage();
      });
    }

    if (
      stickyNextContainerButton &&
      !stickyNextContainerButton.dataset.listenerAttached
    ) {
      stickyNextContainerButton.dataset.listenerAttached = "true";
      stickyNextContainerButton.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.openNextContainerWithoutImage();
      });
    }
  }

  openNextContainerWithoutImage() {
    const containers = this.querySelectorAll(
      ".custom-options-enterprise .option"
    );
    const nextContainer = Array.from(containers).find(
      (container) => !container.querySelector(".design img")
    );

    if (nextContainer) {
      nextContainer.querySelector(".design")?.click();
    }
  }

  setupSwatchListeners() {
    const swatchOptions = document.querySelectorAll(".hulkapps_swatch_option");

    swatchOptions.forEach((swatchOption) => {
      // Prüfen ob bereits Listener vorhanden
      if (swatchOption.dataset.listenerAttached) return;
      swatchOption.dataset.listenerAttached = "true";

      swatchOption.addEventListener("click", () =>
        this.handleSwatchClick(swatchOption)
      );
    });

    // Option Click Handler
    this.querySelectorAll(".option").forEach((option) => {
      if (option.dataset.clickListenerAttached) return;
      option.dataset.clickListenerAttached = "true";

      option.addEventListener("click", () => this.handleOptionClick(option));
    });
  }

  handleSwatchClick(swatchOption) {
    const loader = this.querySelector(".loader");
    const blurOverlay = this.querySelector("#blur-overlay");

    if (loader) loader.style.display = "grid";
    if (blurOverlay) blurOverlay.style.display = "block";

    const imgSrc = swatchOption.querySelector(".swatch_radio")?.dataset.image;
    const ariaLabel = swatchOption.closest("[aria-label]");
    const ariaLabelValue = ariaLabel?.getAttribute("aria-label") || "";

    // Slot-Auswahl ausschließlich über die explizite _pendingSlot-Property.
    // Diese wird in setActiveSlot vom User-Klick gesetzt und überlebt
    // CSS-Klassen-Resets durch andere Funktionen (z.B. checkMatchingOpt).
    //
    // KEIN Fallback auf .option.active oder this.querySelector(".option").
    // Begründung: Hulk hängt mitunter zwei Click-Listener auf dieselbe Swatch
    // (durch wiederholte setupSwatchListeners-Aufrufe oder durch eigene
    // Hulk-Delegations). Beim ersten Aufruf wird _pendingSlot verarbeitet und
    // zurückgesetzt - beim zweiten wäre _pendingSlot null. Ein Fallback auf
    // den ersten Slot überschreibt dann fälschlich Slot 1 (siehe User-Bug-
    // Report mit Console-Logs vom 29.04.2026: "writingTo option-1" beim
    // zweiten Doppelaufruf, obwohl Slot 2 oder 3 das Ziel war).
    if (!this._pendingSlot || !this.contains(this._pendingSlot)) {
      return;
    }
    const activeOption = this._pendingSlot;

    const optionText = activeOption.querySelector("p");
    if (optionText) {
      optionText.textContent = this.cleanPriceText(ariaLabelValue);
    }

    const designDiv = activeOption.querySelector(".design");
    if (designDiv && imgSrc) {
      designDiv.innerHTML = `<img src="${imgSrc}" style="width: 100%; height: auto;">`;
      designDiv.classList.add("hide-after");
      designDiv.style.backgroundColor = "white";
    }

    // Nach erfolgreicher Auswahl: _pendingSlot zurücksetzen und .active von
    // allen Slots entfernen. getCurrentActiveCont setzt den schwarzen
    // "next-container"-Rahmen dann sauber an den nächsten leeren Slot (oder
    // entfernt ihn ganz, wenn alle voll sind).
    this._pendingSlot = null;
    this.querySelectorAll(".option").forEach((opt) =>
      opt.classList.remove("active")
    );

    this.updateBannerMessage();
    this.closeDrawer();
    this.updateButtonVisibility();
    this.checkAndAppendContainers();
    this.getCurrentActiveCont();
  }

  /**
   * Markiert den Slot, der bei der nächsten Swatch-Auswahl gefüllt/ersetzt wird.
   *
   * Auswahl-Regel:
   * - Klick auf einen GEFÜLLTEN Slot → genau dieser Slot wird Target
   *   (gezielter Austausch der bereits gewählten Farbe).
   * - Klick auf einen LEEREN Slot → der NÄCHSTE LEERE Server-Slot wird Target
   *   (sequenzielles Befüllen von Slot 1, 2, 3 ... egal auf welchen leeren
   *   Slot der User klickt).
   *
   * Bonus-Slots ([data-new-container]) werden bei der "next empty"-Suche
   * ignoriert, sonst würde der "Optionale Farbe"-Slot den Auswahl-Pointer
   * stehlen.
   *
   * `_pendingSlot` ist die zuverlässige Referenz für handleSwatchClick und
   * überlebt CSS-Klassen-Resets (z.B. checkMatchingOpt entfernt regelmäßig
   * alle .active-Klassen). `.option.active` bleibt zusätzlich für bestehendes
   * CSS / visuelles Feedback.
   */
  setActiveSlot(clickedOption) {
    this.querySelectorAll(".option").forEach((opt) =>
      opt.classList.remove("active")
    );

    let target = null;
    if (clickedOption) {
      const clickedIsFilled = !!clickedOption.querySelector(".design img");
      if (clickedIsFilled) {
        target = clickedOption;
      } else {
        const firstEmptyServerSlot = Array.from(
          this.querySelectorAll(".option:not([data-new-container])")
        ).find((opt) => !opt.querySelector(".design img"));
        target = firstEmptyServerSlot || clickedOption;
      }
    }

    this._pendingSlot = target;

    if (target) {
      target.classList.add("active");
    }

    this.getCurrentActiveCont();
  }

  handleOptionClick(option) {
    this.setActiveSlot(option);

    this.updateBannerMessage();
    this.updateSelectionStatus();
    this.updateButtonVisibility();
    this.checkAndAppendContainers();
  }

  setupDrawer() {
    const sideDrawer = this.querySelector("#side-drawer");
    const blurOverlay = this.querySelector("#blur-overlay");

    if (!sideDrawer) return;

    setTimeout(() => {
      const designs = this.querySelectorAll(
        ".custom-options-enterprise .option .design"
      );

      designs.forEach((container) => {
        if (container.dataset.drawerListenerAttached) return;
        container.dataset.drawerListenerAttached = "true";

        container.addEventListener("click", (event) => {
          // Slot-Markierung VOR stopPropagation: setupSwatchListeners hängt den
          // .option-Listener (-> handleOptionClick) am Outer-Element. Das hier
          // ist der innere .design-Click-Listener, der gleich event.stopPropagation()
          // ruft -> der Outer-Listener feuert nicht. Daher manuell den geklickten
          // Slot markieren, damit handleSwatchClick weiß, in welchen Slot es die
          // Farbe schreiben soll (gezielter Austausch bei bereits gefüllten Slots).
          const clickedOption = container.closest(".option");
          if (clickedOption) {
            this.setActiveSlot(clickedOption);
          }

          document.body.classList.add("no-scroll");
          event.preventDefault();
          event.stopPropagation();
          sideDrawer.classList.toggle("open");
          if (blurOverlay) {
            blurOverlay.style.display = sideDrawer.classList.contains("open")
              ? "block"
              : "none";
            blurOverlay.style.zIndex = "9";
          }

          // Peek-Animation: wenn Drawer öffnet, kurz auf Slide 2 springen
          // und zurück → signalisiert dem User dass es mehrere Bilder gibt.
          if (sideDrawer.classList.contains("open")) {
            this._playPeekAnimation();
          }

          setTimeout(() => {
            this.updateSelectionStatus();
            this.checkMatchingOpt();
            this.updateButtonVisibility();
            // checkMatchingOpt() entfernt alle .active-Klassen (siehe Zeile 765).
            // Damit der vom User angeklickte Slot (für gezielten Austausch bei
            // vollen Slots) als active erhalten bleibt, setActiveSlot hier erneut
            // aufrufen. Sonst fällt handleSwatchClick auf this.querySelector(".option")
            // (= erster Slot) zurück und tauscht immer Slot 1 aus.
            if (clickedOption) {
              this.setActiveSlot(clickedOption);
            }
          }, 100);
        });
      });

      // Click outside to close
      document.addEventListener("click", (event) => {
        if (
          sideDrawer.classList.contains("open") &&
          !sideDrawer.contains(event.target)
        ) {
          const isDesignClick = event.target.closest(".design");
          if (!isDesignClick) {
            this.closeDrawer();
          }
        }
      });
    }, 900);
  }

  closeDrawer() {
    const sideDrawer = this.querySelector("#side-drawer");
    const blurOverlay = this.querySelector("#blur-overlay");
    const loader = this.querySelector(".loader");

    sideDrawer?.classList.remove("open");
    document.body.classList.remove("no-scroll");
    this._peekPlayed = false;

    this.updateSelectionStatus();
    this.updateButtonVisibility();

    setTimeout(() => {
      if (loader) loader.style.display = "none";
      if (blurOverlay) {
        blurOverlay.style.display = "none";
        blurOverlay.style.zIndex = "99999999999";
      }
    }, 900);

    this.updateBannerMessage();
    this.createCustomContainerLabels();
    this.getDataCount();
  }

  /**
   * Sammelt Variant-Infos aus dem Hulk-DOM.
   * Pro Swatch-Label findet sich ein <input data-variant-id data-image
   * data-conditional-value> und ein .hulkapps-tooltip.
   *
   * Returns Array<{label, tooltip, colorName, variantId, hulkImage}>.
   */
  _collectSwatchInfo() {
    const labels = document.querySelectorAll(".hulkapps_swatch_option");
    const result = [];

    labels.forEach((label) => {
      const tooltip = label.querySelector(".hulkapps-tooltip");
      if (!tooltip) return;

      const input = label.querySelector("input[data-variant-id]");
      const variantId = input?.dataset.variantId || null;
      const colorName =
        input?.dataset.conditionalValue ||
        tooltip.textContent.trim() ||
        label.getAttribute("aria-label")?.trim() ||
        "";
      const hulkImage =
        input?.dataset.image ||
        this.parsedColorOptions?.swatchImages?.[colorName] ||
        null;

      result.push({ label, tooltip, colorName, variantId, hulkImage });
    });

    return result;
  }

  /**
   * Holt das gesamte Variant-ID -> Bilder Mapping aus der öffentlichen
   * Collection-JSON-API. Alle Farb-Collections werden parallel gefetcht
   * und zu einem einzigen Mapping zusammengeführt - ganz ohne Auth/Token.
   *
   * - Nutzt sessionStorage als Cache (TTL 24h).
   * - Bei Fehler einzelner Collections: wird übersprungen, andere laufen weiter.
   * - Bei Fehler: liefert {} zurück, das Karussell fällt auf das Hulk-Bild
   *   zurück (= 1-Slide-Verhalten).
   *
   * Returns: { byVariantId: { [variantId]: string[] }, byHandle: { [handle]: string[] } }
   */
  async fetchColorProductImages() {
    const COLLECTION_HANDLES = [
      "shellac",
      "farbgele",
      "no-file-gel",
      "aufbaugele",
      "bottle-gel",
      "rubber-base",
      "base-top-coat-kopie",
      "top",
    ];
    const cacheKey = `gdn:colorImages:v5:all`;
    const cacheTTL = 24 * 60 * 60 * 1000;

    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.timestamp && Date.now() - parsed.timestamp < cacheTTL) {
          return parsed.data || {};
        }
      }
    } catch (e) {
      // sessionStorage nicht verfügbar oder JSON kaputt -> neu laden
    }

    const byVariantId = {};
    const byHandle = {};

    const results = await Promise.allSettled(
      COLLECTION_HANDLES.map((handle) =>
        fetch(`/collections/${handle}/products.json?limit=250`, {
          headers: { Accept: "application/json" },
        }).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status} for ${handle}`);
          return r.json();
        })
      )
    );

    results.forEach((result, i) => {
      if (result.status === "rejected") {
        console.warn(
          `SetConfigurator: Collection "${COLLECTION_HANDLES[i]}" fetch failed`,
          result.reason
        );
        return;
      }
      const products = result.value.products || [];
      products.forEach((product) => {
        const imageUrls = (product.images || [])
          .map((img) => img && img.src)
          .filter(Boolean);
        if (imageUrls.length === 0) return;

        byHandle[product.handle] = imageUrls;

        (product.variants || []).forEach((variant) => {
          if (variant && variant.id) {
            byVariantId[String(variant.id)] = imageUrls;
          }
        });
      });
    });

    const mapping = { byVariantId, byHandle };

    try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ timestamp: Date.now(), data: mapping })
      );
    } catch (e) {
      // sessionStorage voll -> trotzdem in-memory verfügbar
    }

    return mapping;
  }

  _normalizeColorImageCache(cache) {
    if (!cache || typeof cache !== "object") {
      return { byVariantId: {}, byHandle: {} };
    }
    if (cache.byVariantId || cache.byHandle) {
      return {
        byVariantId: cache.byVariantId || {},
        byHandle: cache.byHandle || {},
      };
    }
    return { byVariantId: cache, byHandle: {} };
  }

  _slugifyColorFragment(text) {
    return String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  _productHandleFromColorName(colorName) {
    const raw = String(colorName || "").trim();
    if (!raw) return null;

    const { slotSingular } = this.getSlotConfig();
    let colorPart = raw;

    const stripLeading = (prefix) => {
      const re = new RegExp(`^${prefix}\\s*`, "i");
      if (re.test(colorPart)) {
        colorPart = colorPart.replace(re, "").trim();
      }
    };

    if (/top\s*coat/i.test(slotSingular) || /^top\s*coat/i.test(raw)) {
      stripLeading("top coat");
      const slug = this._slugifyColorFragment(colorPart);
      return slug ? `top-coat-${slug}` : null;
    }

    if (/rubber\s*base/i.test(slotSingular) || /^rubber\s*base/i.test(raw)) {
      stripLeading("rubber base");
      return this._slugifyColorFragment(colorPart) || null;
    }

    if (/^shellac/i.test(raw)) {
      stripLeading("shellac");
      return this._slugifyColorFragment(colorPart) || null;
    }

    if (/no\s*file\s*gel/i.test(raw)) {
      stripLeading("no file gel");
      const slug = this._slugifyColorFragment(colorPart);
      return slug ? `no-file-gel-${slug}` : null;
    }

    if (/^farbgel/i.test(raw)) {
      stripLeading("farbgel");
      const slug = this._slugifyColorFragment(colorPart);
      return slug ? `farbgel-${slug}` : null;
    }

    return null;
  }

  _resolveCarouselStorefrontImages(cache, variantId, colorName) {
    const { byVariantId, byHandle } = this._normalizeColorImageCache(cache);
    let images =
      variantId != null ? byVariantId[String(variantId)] : undefined;

    if (!images || images.length <= 1) {
      const handle = this._productHandleFromColorName(colorName);
      if (handle && byHandle[handle]?.length) {
        images = byHandle[handle];
      }
    }

    return (images || []).slice(1, 3);
  }

  /**
   * Liefert eine resized + zentriert quadratisch zugeschnittene Variante
   * einer Shopify-CDN-URL.
   *
   * Crop-Center sorgt dafür, dass Hochformat-Produktbilder (Finger, Hand)
   * und Querformat-Hulk-Bilder beide als Quadrat dargestellt werden, also
   * im 200×200 Cell-Layout gleich aussehen.
   *
   * Bei Nicht-Shopify-URLs (oder fehlerhaften Inputs) wird die URL
   * unverändert zurückgegeben - das Karussell zeigt dann das Original.
   */
  _resizeShopifyImage(url, size = 600) {
    if (!url || typeof url !== "string") return url;
    if (!url.includes("cdn.shopify.com") && !url.includes("/files/")) {
      return url;
    }
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}width=${size}&height=${size}&crop=center`;
  }

  /**
   * Spielt beim Öffnen des Drawers eine kurze "Peek"-Animation auf dem
   * ERSTEN sichtbaren Karussell: Slider schiebt sich ~35% nach links
   * (linke Kante von Slide 2 lugt rein), dann gleitet zurück.
   * Signalisiert dem User einmalig dass es mehr Bilder gibt.
   */
  _playPeekAnimation() {
    setTimeout(() => {
      const firstLabel = document.querySelector(
        ".hulkapps_swatch_option[data-carousel-built='true']"
      );
      if (!firstLabel) return;

      const carousel = firstLabel.querySelector(".main-carousel");
      if (!carousel) return;

      const flkty = window.Flickity && window.Flickity.data(carousel);
      if (!flkty || flkty.cells.length < 2) return;

      const slider = carousel.querySelector(".flickity-slider");
      if (!slider) return;

      this._peekPlayed = true;

      // Halb rausschieben: ~35% nach links → Slide 2 lugt von rechts rein.
      slider.style.transition = "transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
      slider.style.transform = "translateX(-50%)";

      // Nach kurzem Innehalten zurückschieben.
      setTimeout(() => {
        slider.style.transform = "";
        setTimeout(() => {
          slider.style.transition = "";
        }, 450);
      }, 500);
    }, 450);
  }

  /**
   * Beobachtet Swatch-Labels via IntersectionObserver und baut Karussells
   * nur wenn sie im (oder nahe am) Viewport sind. Verhindert den 30s-Hänger
   * durch 240 simultane Flickity-Inits beim Drawer-Open.
   */
  _setupCarouselObserver() {
    if (this._carouselObserver) return;

    this._carouselObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._buildCarouselForLabel(entry.target);
          }
        });
      },
      { rootMargin: "100px", threshold: 0.01 }
    );

    const observeLabels = () => {
      document.querySelectorAll(".hulkapps_swatch_option").forEach((label) => {
        if (label.dataset.carouselObserved === "true") return;
        label.dataset.carouselObserved = "true";
        this._carouselObserver.observe(label);
      });
    };

    observeLabels();

    const hulkContainer = document.querySelector(
      '[id^="hulkapps_custom_options_"]'
    );
    if (!hulkContainer || this._carouselMutationObserver) return;

    this._carouselMutationObserver = new MutationObserver(() => observeLabels());
    this._carouselMutationObserver.observe(hulkContainer, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Hängt einen Delegated-Hover-Listener an den Hulk-Container, der pro
   * Swatch-Label das Karussell **on demand beim ersten Hover** baut.
   *
   * Vorteil gegenüber bulk-init aller 240 Swatches:
   *   - Page-Load und Drawer-Open bleiben snappy (keine CPU-Spitze).
   *   - Flickity initialisiert auf einem sichtbaren Container -> Cells
   *     haben korrekte Breite, kein 0-width-Bug.
   *   - Wir bauen nur Karussells, die der User tatsächlich ansieht.
   */
  _setupCarouselHovers() {
    // Delegierter Listener: ein einziger Hook für alle Swatches.
    // pointerenter feuert auch auf Touch-Devices beim ersten Antippen.
    const handler = (event) => {
      const label = event.target.closest(".hulkapps_swatch_option");
      if (!label || label.dataset.carouselBuilt === "true") return;
      this._buildCarouselForLabel(label);
    };

    document.addEventListener("pointerover", handler, { passive: true });
    this._onCarouselPointerOver = handler;
  }

  /**
   * Baut ein einzelnes Karussell für ein Swatch-Label. Idempotent durch
   * `data-carousel-built`.
   *
   * Wird beim ersten Hover gerufen - kleine Latenz beim ersten Mal,
   * danach instant. Die Storefront-Bilder kommen aus dem prefetched
   * Cache (siehe init()), also keine Netzwerk-Wartezeit hier.
   */
  async _buildCarouselForLabel(label) {
    if (
      label.dataset.carouselBuilt === "true" ||
      label.dataset.carouselBuilding === "true"
    ) {
      return;
    }
    label.dataset.carouselBuilding = "true";

    try {
      await this.loadFlickity();
      if (!window.Flickity) return;

      const tooltip = label.querySelector(".hulkapps-tooltip");
      if (!tooltip || !tooltip.parentNode) return;

      // Variant-ID kann auf <input>, <div> oder direkt am Label sitzen.
      // Wir suchen das erste Element im Label-Subtree mit data-variant-id.
      const variantHolder =
        label.querySelector("[data-variant-id]") ||
        (label.dataset.variantId ? label : null);
      const variantId = variantHolder?.dataset.variantId || null;

      const colorName =
        variantHolder?.dataset.conditionalValue ||
        tooltip.textContent.trim() ||
        label.getAttribute("aria-label")?.trim() ||
        "";
      const hulkImage =
        variantHolder?.dataset.image ||
        this.parsedColorOptions?.swatchImages?.[colorName] ||
        null;

      const productImages = await this.fetchColorProductImages();
      const storefrontImages = this._resolveCarouselStorefrontImages(
        productImages,
        variantId,
        colorName
      );

      // Wenn weder Hulk- noch Storefront-Bilder da sind, Tooltip
      // unverändert lassen (= alter Schriftzug-only Zustand).
      if (!hulkImage && storefrontImages.length === 0) return;

      const flickityContainer = document.createElement("div");
      flickityContainer.classList.add("main-carousel");

      // Slide 1: Hulk-Bild als Hero - auch durch den Crop-Resizer
      // jagen, damit alle 5 Slides konsistent quadratisch sind.
      if (hulkImage) {
        const cell = document.createElement("div");
        cell.classList.add("carousel-cell");
        const img = document.createElement("img");
        img.src = this._resizeShopifyImage(hulkImage, 600);
        img.alt = colorName;
        cell.appendChild(img);
        flickityContainer.appendChild(cell);
      } else {
        const cell = document.createElement("div");
        cell.classList.add("carousel-cell");
        cell.appendChild(tooltip.cloneNode(true));
        flickityContainer.appendChild(cell);
      }

      // Slides 2..n: Storefront-Produktbilder (eager src - Karussell
      // wird ja gerade angesehen).
      storefrontImages.forEach((imgUrl) => {
        const cell = document.createElement("div");
        cell.classList.add("carousel-cell");
        const img = document.createElement("img");
        img.src = this._resizeShopifyImage(imgUrl, 600);
        img.alt = colorName;
        cell.appendChild(img);
        flickityContainer.appendChild(cell);
      });

      tooltip.parentNode.replaceChild(flickityContainer, tooltip);

      const flickityInstance = new window.Flickity(flickityContainer, {
        cellAlign: "center",
        contain: true,
        wrapAround: true,
        prevNextButtons: false,
        pageDots: true,
        dragThreshold: 10,
        friction: 0.5,
        selectedAttraction: 0.12,
        freeScroll: false,
        groupCells: 1,
        imagesLoaded: true,
        setGallerySize: false, // Wir steuern Höhe via CSS aspect-ratio
      });

      // Default: Karussell startet auf Slide 0 (Hulk-Bild als Hero).
      // Beim Hover wechselt es auf Slide 1 (1. Storefront-Bild),
      // beim Verlassen zurück auf Slide 0.
      if (storefrontImages.length > 0) {
        label.addEventListener("mouseenter", () => {
          flickityInstance.resize();
          flickityInstance.select(1);
        });
        label.addEventListener("mouseleave", () =>
          flickityInstance.select(0)
        );
      }

      // Page-dot-Klicks innerhalb dieses Karussells nicht weiterleiten.
      flickityContainer
        .querySelectorAll(".flickity-page-dots")
        .forEach((dot) => {
          dot.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
          });
        });

      label.dataset.carouselBuilt = "true";

      // Erster Build: wenn Drawer bereits offen ist und der Peek noch
      // nicht gespielt wurde, jetzt nachholen.
      const isFirstCarousel =
        !this._peekPlayed &&
        document.querySelectorAll(".hulkapps_swatch_option[data-carousel-built='true']")
          .length === 1;

      if (isFirstCarousel) {
        const sideDrawer = this.querySelector("#side-drawer");
        if (sideDrawer?.classList.contains("open")) {
          this._playPeekAnimation();
        }
      }
    } catch (error) {
      console.warn(
        "SetConfigurator: Carousel build failed for label",
        label,
        error
      );
    } finally {
      delete label.dataset.carouselBuilding;
    }
  }

  async initSearch() {
    await this.loadFuse();
    if (!window.Fuse) return;

    const searchInput = this.querySelector(".hulk-input");
    const labels = document.querySelectorAll(".hulkapps_swatch_option");

    if (!searchInput || labels.length === 0) return;

    const options = Array.from(labels).map((label) => {
      const optionText =
        label.querySelector(".swatch_tooltip_title p")?.textContent.trim() ||
        "";
      return { text: optionText, label: label };
    });

    const fuse = new window.Fuse(options, {
      keys: ["text"],
      includeScore: true,
      threshold: 0.4,
    });

    searchInput.addEventListener("input", () => {
      const searchTerm = searchInput.value.trim().toLowerCase();

      if (searchTerm === "") {
        labels.forEach((label) => (label.style.display = ""));
        return;
      }

      const results = fuse.search(searchTerm);
      labels.forEach((label) => (label.style.display = "none"));

      results.forEach((result) => {
        result.item.label.style.display = "";
      });
    });
  }

  createCustomLabels() {
    if (!this.parsedColorOptions?.hulkOptions) return;

    try {
      const hulkOptions = this.parsedColorOptions.hulkOptions;

      for (const [key, value] of Object.entries(hulkOptions)) {
        document.querySelectorAll("label[aria-label]").forEach((label) => {
          const titleElement = label.querySelector(".swatch_tooltip_title p");
          if (titleElement) {
            const labelText = titleElement.textContent.trim();
            const textAtPosition = label.querySelector(".hulkapps-tooltip");

            if (key === labelText && textAtPosition) {
              const customLabel = document.createElement("div");
              customLabel.classList.add("hulk-label");
              customLabel.innerText = value;
              label.style.position = "relative";
              textAtPosition.appendChild(customLabel);
            }
          }
        });
      }
    } catch (error) {
      console.warn("SetConfigurator: Custom labels creation failed", error);
    }
  }

  createCustomContainerLabels() {
    if (!this.parsedColorOptions?.options) return;

    try {
      const badgeOptions = this.parsedColorOptions.options;

      badgeOptions.forEach((badge, index) => {
        const optionKey = `option-${index + 1}`;

        if (badge.hasOwnProperty(optionKey)) {
          const value = badge[optionKey];

          this.querySelectorAll(".custom-options-enterprise .option").forEach(
            (label) => {
              const optClasses = label.classList;
              optClasses.forEach((optClass) => {
                if (
                  optClass.startsWith("option-") ||
                  optClass.startsWith("discount-")
                ) {
                  const textAtPosition = label.querySelector(".design");
                  if (
                    optionKey === optClass &&
                    textAtPosition &&
                    !textAtPosition.querySelector(".container-label")
                  ) {
                    const customLabel = document.createElement("div");
                    customLabel.classList.add("container-label");
                    customLabel.innerText = value;
                    label.style.position = "relative";
                    textAtPosition.appendChild(customLabel);
                  }
                }
              });
            }
          );
        }
      });
    } catch (error) {
      console.warn("SetConfigurator: Container labels creation failed", error);
    }
  }

  initCustomFields() {
    if (!this.parsedCustomFields?.fields) return;

    const customFieldsContainer = document.querySelector(
      ".custom_options_fields"
    );
    if (!customFieldsContainer) return;

    customFieldsContainer.style.display = "block";

    try {
      const fieldOptions = this.parsedCustomFields.fields;
      let variantPointsHTML = "";

      if (
        fieldOptions.variant_points &&
        Array.isArray(fieldOptions.variant_points)
      ) {
        fieldOptions.variant_points.forEach((variantPoint) => {
          Object.keys(variantPoint).forEach((key) => {
            if (key.startsWith("text_line_") && variantPoint[key]) {
              variantPointsHTML += `
                <div class="in-bp">
                  <div class="bp-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 17 14" fill="none">
                      <path d="M1 9.57143L4.33333 13L16 1" stroke="#272727"></path>
                    </svg>
                  </div>
                  <div class="bp-text">
                    <span>${variantPoint[key]}</span>
                  </div>
                </div>
              `;
            }
          });
        });
      }

      const fieldHTML = `
        <div class="bullets-head">
          <span>${fieldOptions.title || ""}</span>
          <button class="whats-in-set bullets-question_mark">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </button>
        </div>
        <div class="starter-set-bp bp-grid">
          ${variantPointsHTML}
        </div>
      `;

      customFieldsContainer.insertAdjacentHTML("beforeend", fieldHTML);

      // What's in set button handler
      const whatsInSetBtn =
        customFieldsContainer.querySelector(".whats-in-set");
      if (whatsInSetBtn) {
        whatsInSetBtn.addEventListener("click", (event) =>
          this.handleWhatsInSetClick(event)
        );
      }
    } catch (error) {
      console.warn(
        "SetConfigurator: Custom fields initialization failed",
        error
      );
    }
  }

  handleWhatsInSetClick(event) {
    const sideDrawer = this.querySelector("#side-drawer");
    const blurOverlay = this.querySelector("#blur-overlay");

    document.body.classList.add("no-scroll");
    event.preventDefault();
    event.stopPropagation();
    sideDrawer?.classList.toggle("open");

    if (blurOverlay) {
      blurOverlay.style.display = sideDrawer?.classList.contains("open")
        ? "block"
        : "none";
      blurOverlay.style.zIndex = "9";
    }

    // Hide hulk options, show sections
    const hulkCustomOptions = document.querySelector(
      '[id^="hulkapps_custom_options_"]'
    );
    const hulkInputWrapper = this.querySelector(".hulk-input-wrapper");
    const topSection = this.querySelector(".top-section");
    const lowerBar = this.querySelector(".lower-bar");
    const lowerSection = this.querySelector(".lower-section");

    if (hulkCustomOptions) hulkCustomOptions.style.display = "none";
    if (hulkInputWrapper) hulkInputWrapper.style.display = "none";
    if (topSection) topSection.style.display = "flex";
    if (lowerBar) lowerBar.style.display = "flex";
    if (lowerSection) lowerSection.style.display = "flex";

    this.populateSideDrawerContent();
  }

  populateSideDrawerContent() {
    if (!this.parsedCustomFields?.fields?.side_drawer_content) return;

    try {
      const content = this.parsedCustomFields.fields.side_drawer_content;

      const topSection = this.querySelector(".top-section");
      const lowerSection = this.querySelector(".lower-section");
      const lowerSectionHeader = this.querySelector(".lower-bar");
      const topSectionHeader = this.querySelector(".top-bar .img-container");

      if (topSection) topSection.innerHTML = "";
      if (lowerSection) lowerSection.innerHTML = "";

      if (lowerSectionHeader && content.side_drawer_lower_section_title) {
        lowerSectionHeader.innerHTML = content.side_drawer_lower_section_title;
      }

      if (topSectionHeader && content.side_drawer_top_section_title) {
        topSectionHeader.textContent = content.side_drawer_top_section_title;
      }

      if (content.side_drawer_top_section?.[0]) {
        this.processSection(content.side_drawer_top_section[0], topSection);
      }

      if (content.side_drawer_lower_section?.[0]) {
        this.processSection(content.side_drawer_lower_section[0], lowerSection);
      }
    } catch (error) {
      console.warn(
        "SetConfigurator: Side drawer content population failed",
        error
      );
    }
  }

  processSection(sectionData, parentElement) {
    if (!parentElement) return;

    for (let key in sectionData) {
      const section = sectionData[key];

      if (section) {
        const span = document.createElement("span");

        if (section.image) {
          const img = document.createElement("img");
          img.src = section.image;
          img.alt = section.title || key;
          img.classList.add("section-img");
          span.appendChild(img);
        }

        if (section.title) {
          const title = document.createElement("h3");
          title.classList.add("sectionTitle");
          title.textContent = section.title;
          span.appendChild(title);
        }

        if (section.details) {
          const details = document.createElement("p");
          details.textContent = section.details;
          span.appendChild(details);
        }

        if (typeof section === "object" && !Array.isArray(section)) {
          this.processSection(section, span);
        }

        parentElement.appendChild(span);
      }
    }
  }

  calculateDifference() {
    setTimeout(() => {
      try {
        // Spezifisch den Preis-Container des Hauptprodukts finden
        const priceContainer = document.querySelector(
          ".product-meta__price-list-container"
        );
        if (!priceContainer) return;

        // Innerhalb des Containers nach den Preisen suchen
        const partPriceText = priceContainer.querySelector(
          ".price.price--compare"
        )?.innerText;
        const wholePriceText = priceContainer.querySelector(
          ".price.price--highlight"
        )?.innerText;

        if (!partPriceText || !wholePriceText) return;

        // Deutsches Zahlenformat korrekt parsen
        const parseGermanPrice = (text) => {
          return parseFloat(
            text
              .replace(/[^0-9.,]/g, "") // Nur Zahlen, Punkte und Kommas behalten
              .replace(/\./g, "") // ALLE Punkte entfernen (Tausendertrennzeichen)
              .replace(",", ".") // Komma durch Punkt ersetzen (Dezimaltrennzeichen)
          );
        };

        const partPrice = parseGermanPrice(partPriceText);
        const wholePrice = parseGermanPrice(wholePriceText);

        if (isNaN(partPrice) || isNaN(wholePrice)) return;

        const discountPercentage = Math.round(
          ((partPrice - wholePrice) / partPrice) * 100
        );

        const discountEl = document.querySelector(".discountPricePrecentage");
        if (discountEl) {
          const label =
            discountEl.dataset && discountEl.dataset.discountLabel
              ? discountEl.dataset.discountLabel.trim()
              : "";
          discountEl.innerText = label
            ? `${label} ${discountPercentage}%`
            : `${discountPercentage}%`;
        }
      } catch (error) {
        // Silently fail
      }
    }, 900);
  }

  observePriceChanges() {
    let previousPrice = document.querySelector(
      ".price.price--highlight .money"
    )?.innerText;

    // Statt setInterval verwenden wir MutationObserver
    const priceElement = document.querySelector(".price.price--highlight");
    if (!priceElement) return;

    const observer = new MutationObserver(() => {
      const currentPrice = document.querySelector(
        ".price.price--highlight .money"
      )?.innerText;
      if (currentPrice !== previousPrice) {
        previousPrice = currentPrice;
        this.calculateDifference();
      }
    });

    observer.observe(priceElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }
}

// Web Component registrieren
customElements.define("set-configurator", SetConfigurator);
